/**
*
* PendingForm
*
*/

import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, SplitButton, Dropdown } from 'react-bootstrap';
import { useIntl, FormattedMessage } from 'react-intl';
import DateTimeString from 'components/DateTimeString/index.jsx';
import Form4 from 'components/interaction/Form4/index.jsx';
import { SpinnerDiv } from 'components/Spinner/index.jsx';
import { SCHEDULE_DELETE, SCHEDULE_DRAFT, SCHEDULE_RELEASED, SCHEDULE_SCHEDULED } from 'components/Post/constants.js';
import useDialog from 'containers/Dialog/useDialog.js';
import usePendingForm from './usePendingForm.js';
import SelectDate from './SelectDate.jsx';
import messages from './messages.js';

const PendingForm = forwardRef((props, ref) => {
  const { noPending, onDelete, onSubmit, onChange, onSelectingDate, noDirectSend, children, ...formProps } = props;
  const { defaultValues, onCancel, submitText = messages.post } = formProps;
  const { dlgClick } = useDialog();

  const { show, setShow, working, handleSubmit, handleDate, handleClick } = usePendingForm(onSubmit, onDelete, defaultValues, noDirectSend);
  const intl = useIntl();
  const [values, setValues] = useState(defaultValues);

  useEffect(() => {
    if (onSelectingDate) {
      onSelectingDate(show);
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(data) {
    setValues(data);
    if (onChange) {
      onChange(data);
    }
  }

  function handleDelete(val, confirmText) {
    dlgClick({
      message: confirmText,
      confirmLabel: messages.remove,
      confirmStyle: 'danger',

    }, () => handleClick(val, SCHEDULE_DELETE));
  }

  function getSubmitMessage(isScheduled) {
    if (isScheduled) {
      return messages.save;
    }
    return noDirectSend ? messages.saveDraft : submitText;
  }

  function getAction(action) {
    const { valid = true, loading } = action.props;
    const { id, title, status, scheduled } = { ...defaultValues, ...values };
    const canSubmit = valid || Boolean(id);
    const scheduledDate = scheduled && new Date(scheduled);
    const isScheduled = status === SCHEDULE_SCHEDULED;
    const scheduleMessage = isScheduled ? messages.reschedule : messages.schedulePost;
    const confirmText = values.title ? { ...messages.deleteItem, values: { title } } : messages.deleteUntitled;

    return (
      <>
        <div className="d-flex justify-content-end">
          <Button variant="light" onClick={onCancel} disabled={working}><FormattedMessage {...messages.cancel} /></Button>
          <SpinnerDiv show={working || loading}>
            <SplitButton
              title={intl.formatMessage(getSubmitMessage(isScheduled))}
              variant="primary"
              className="ms-3"
              type="submit"
              align="end"
              id="submit-dropdown"
              disabled={!canSubmit || working || loading}
            >
              {!noDirectSend && (!isScheduled ? (
                <Dropdown.Item as="button" type="button" onClick={() => handleClick(values, SCHEDULE_DRAFT)}>
                  <FormattedMessage {...messages.saveDraft} />
                </Dropdown.Item>
              ) : (
                <Dropdown.Item as="button" type="submit" onClick={() => handleClick(values, SCHEDULE_RELEASED)}>
                  <FormattedMessage {...messages.sendNow} />
                </Dropdown.Item>
              ))}
              <Dropdown.Item as="button" type="submit" onClick={() => handleClick(values, SCHEDULE_SCHEDULED)}>
                <FormattedMessage {...scheduleMessage} />
              </Dropdown.Item>
              {Boolean(id) && (
                <Dropdown.Item as="button" type="button" onClick={() => handleDelete(values, confirmText)}>
                  <FormattedMessage {...messages.delete} />
                </Dropdown.Item>
              )}
            </SplitButton>
          </SpinnerDiv>
        </div>
        {status === SCHEDULE_SCHEDULED && (
          <div className="text-end help-block">
            { scheduled ? (
              <>
                <FormattedMessage {...messages.sendOn} />
                <DateTimeString date={scheduled} />
              </>
            ) : (
              <span className="text-danger ms-2"><FormattedMessage {...messages.missingDate} /></span>
            )}
          </div>
        )}
        {show && <SelectDate onHide={() => setShow(false)} value={scheduledDate} onChange={(date) => handleDate(values, date)} />}
      </>
    );
  }

  function renderForm(params) {
    const { body, action } = params;
    const pendingEnabled = values.status !== SCHEDULE_RELEASED && !noPending;
    const newParams = pendingEnabled ? { ...params, action: getAction(action) } : params;
    return children ? children(newParams) : (
      <>
        {body}
        <div className="mb-4">
          {action}
        </div>
      </>
    );
  }

  return <Form4 {...formProps} onChange={handleChange} ref={ref} onSubmit={handleSubmit}>{renderForm}</Form4>;
});

PendingForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onChange: PropTypes.func,
  onSelectingDate: PropTypes.func,
  noPending: PropTypes.bool,
  loading: PropTypes.bool,
  submitText: PropTypes.object,
  noDirectSend: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
};

export default PendingForm;
